<template>
  <app-modal
    ref="modal"
    v-bind="$attrs"
    class="modal-container"
    content-class="modal-large"
    title="VNコネクト設定未完了一覧"
  >
    <template v-slot:body="{ cancel }">
      <!-- BODY -->
      <v-card-text class="pa-0">
        <div class="modal-body">
          <v-container>
            <v-row>
              <v-col class="d-flex flex-row justify-end mb-2">
                <v-btn color="primary" @click="resetting"
                  >VNコネクト再設定</v-btn
                >
              </v-col>
            </v-row>
            <v-alert v-if="alertMsg" class="my-2" :text="alertMsg"></v-alert>
            <app-table
              primary-key="cloudVnConnectAct.wnumber"
              :fields="vnConnects.fields"
              :items="sortedCloudVnResetting"
              :selectable="true"
              v-model="vnConnects.selected"
              :hover="true"
              :per-page="-1"
            >
              <template #cell(enumber)="{ item }">
                {{ item.cloudLineInfo.enumber }}
              </template>
              <template #cell(cloudServiceMenu)="{ item }">
                {{
                  $filter.cloudServiceMenuTo(
                    item.cloudLineInfo.cloudServiceMenu
                  )
                }}
              </template>
              <template #cell(wnumber)="{ item }">
                {{ item.cloudVnConnectAct.wnumber }}<br />
                <template v-if="item.cloudVnConnectSby">{{
                  item.cloudVnConnectSby.wnumber
                }}</template>
                <template v-else>-</template>
              </template>
              <template #cell(vnConnectName)="{ item }">
                {{ item.cloudVnConnectAct.vnConnectName }}<br />
                <template v-if="item.cloudVnConnectSby">{{
                  item.cloudVnConnectSby.vnConnectName
                }}</template>
                <template v-else>-</template>
              </template>
              <template #cell(vpnVnCode)="{ item }">
                <template
                  v-if="item.cloudVnConnectCommon.vpnVnCode.charAt(0) !== 'B'"
                >
                  {{
                    `${item.cloudVnConnectCommon.vpnVnCode}(${item.cloudVnConnectCommon.vnType})`
                  }}
                </template>
                <template v-else>
                  {{ item.cloudVnConnectCommon.vpnVnCode }}
                </template>
              </template>
            </app-table>
          </v-container>
        </div>
      </v-card-text>
      <v-divider></v-divider>
      <!-- FOOTER -->
      <v-card-actions>
        <v-list-item class="w-100 pa-1">
          <template v-slot:append>
            <v-btn @click="cancel" variant="outlined" color="primary"
              >戻る</v-btn
            >
          </template>
        </v-list-item>
      </v-card-actions>
    </template>
  </app-modal>
</template>

<script lang="ts">
import type { PropType } from "vue";
import { defineComponent } from "vue";
import { VueFinalModal } from "vue-final-modal";
import type { AppTableHeader } from "@/components/AppTable.vue";
import type {
  CloudVnConnects,
  CloudVnResetting,
} from "@/apis/CloudVnResettingApi";
import cloneDeep from "lodash/cloneDeep";

export default defineComponent({
  name: "CloudVnResettingList",
  props: {
    /** VNコネクト設定未完了一覧情報 */
    cloudVnResetting: {
      type: Object as PropType<CloudVnResetting>,
      required: true,
    },
  },
  data() {
    return {
      vnConnects: {
        fields: [
          {
            key: "enumber",
            title: "回線番号",
            width: "180px",
          },
          {
            key: "cloudServiceMenu",
            title: "メニュー",
            width: "230px",
          },
          {
            key: "wnumber",
            title: "VNコネクト番号(Act/Sby)",
            width: "230px",
          },
          {
            key: "vnConnectName",
            title: "VNコネクト名(Act/Sby)",
            width: "200px",
          },
          {
            key: "vpnVnCode",
            title: "接続先VPN/VNコード",
            width: "260px",
          },
        ] as AppTableHeader<CloudVnResetting>[],
        selected: [] as CloudVnResetting["cloudVnConnectsList"],
      },
      alertMsg: null as string | null,
    };
  },
  computed: {
    sortedCloudVnResetting() {
      return cloneDeep(this.cloudVnResetting.cloudVnConnectsList).sort(
        (a, b) => {
          // 第一優先のenumberの降順
          if (a.cloudLineInfo.enumber < b.cloudLineInfo.enumber) {
            return 1;
          } else if (a.cloudLineInfo.enumber === b.cloudLineInfo.enumber) {
            // 第二優先のcloudServiceMenuの順：KCPS3、AWS、MS、GCP、Oracle、IBM、SFDCの順
            const sortBy = (e: CloudVnConnects): number => {
              switch (e.cloudLineInfo.cloudServiceMenu) {
                case "KCPS3":
                  return 1;
                case "AWS":
                  return 2;
                case "GCP":
                  return 4;
                case "Oracle":
                  return 5;
                case "IBM":
                  return 6;
                case "SFDC":
                  return 7;
                default:
                  return 3;
              }
            };
            const sortA = sortBy(a);
            const sortB = sortBy(b);
            if (sortA === 2 && sortB === 2) {
              return (
                +a.cloudLineInfo.cloudServiceMenu!.replace("MS", "") -
                +b.cloudLineInfo.cloudServiceMenu!.replace("MS", "")
              );
            }
            return sortA - sortB;
          } else {
            return -1;
          }
        }
      );
    },
  },
  methods: {
    /** VNコネクト再設定 */
    async resetting() {
      if (this.vnConnects.selected.length !== 1) {
        this.alertMsg = "VNコネクトを１つ選択してください。";
        return;
      }
      this.alertMsg = null;
      const cloudServiceMenu = this.$filter.cloudServiceMenuTo(
        this.vnConnects.selected[0].cloudLineInfo.cloudServiceMenu
      );
      await this.$confirm(
        `${cloudServiceMenu}のVNコネクト再設定を行います。よろしいですか？`
      );
      await this.$api.cloudVnResetting.postCloudVnResetting(
        this.vnConnects.selected[0].cloudVnConnectAct.wnumber
      );
      await (
        this.$refs.modal as typeof VueFinalModal & { ok: () => void }
      ).ok();
    },
  },
});
</script>
